import { OfficeStatus } from "@prisma/client";
import { useRouter } from "next/router";
import React, { createContext, useContext, useEffect, useState } from "react";

import { localStorage } from "@calcom/lib/webstorage";
import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";

export type OfficeData = RouterOutputs["viewer"]["office"]["getById"];

type OfficeContextType = {
  currentOffice: number | undefined;
  setCurrentOffice: React.Dispatch<React.SetStateAction<number | undefined>>;
  office: OfficeData | undefined;
  isOfficeLoading: boolean;
};

const OfficeContext = createContext<OfficeContextType>({
  currentOffice: undefined,
  setCurrentOffice: () => undefined,
  office: undefined,
  isOfficeLoading: false,
});

const useOfficeContext = (): OfficeContextType => {
  return useContext(OfficeContext);
};

/** When localStorage has an office the user can no longer access, snap to assigned/first allowed and redirect. */
function OfficeAccessGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { currentOffice, setCurrentOffice } = useOfficeContext();
  const { data: session } = trpc.viewer.public.session.useQuery();
  const authed = !!session?.user;
  const { data: user, isFetched: userFetched } = trpc.viewer.me.useQuery(undefined, { enabled: authed });
  const { data: officeOptions } = trpc.viewer.office.getOfficeOptions.useQuery(
    { statuses: [OfficeStatus.ACTIVE, OfficeStatus.PAUSE] },
    { enabled: authed }
  );

  useEffect(() => {
    if (!authed || !router.isReady) return;
    if (router.pathname.startsWith("/auth")) return;
    if (!officeOptions) return;
    if (!userFetched) return;

    const allowedIds = new Set(officeOptions.map((o) => Number(o.value)));

    if (allowedIds.size === 0) {
      if (currentOffice !== undefined) {
        setCurrentOffice(undefined);
        if (typeof window !== "undefined") {
          localStorage.removeItem("currentOffice");
        }
      }
      return;
    }

    if (currentOffice === undefined) return;

    if (allowedIds.has(currentOffice)) return;

    const assigned = user?.currentOffice;
    const fallback =
      assigned != null && assigned !== undefined && allowedIds.has(assigned)
        ? assigned
        : Number(officeOptions[0]?.value);

    if (!Number.isFinite(fallback)) return;

    setCurrentOffice(fallback);

    const destination = user?.isGroupView ? "/home" : "/practice";
    if (router.pathname !== destination) {
      void router.replace(destination);
    }
  }, [
    authed,
    officeOptions,
    currentOffice,
    router,
    setCurrentOffice,
    user?.currentOffice,
    user?.isGroupView,
    userFetched,
  ]);

  return <>{children}</>;
}

const OfficeProvider = (props: { children: React.ReactNode }) => {
  const [currentOffice, setCurrentOffice] = useState<number | undefined>(undefined);
  const { data: session } = trpc.viewer.public.session.useQuery();
  const authed = !!session?.user;

  const { data: office, isLoading: isOfficeLoading } = trpc.viewer.office.getById.useQuery(
    { officeId: currentOffice!, _type: "officeProvider" },
    {
      enabled: !!currentOffice && authed,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const stored = localStorage.getItem("currentOffice");
    if (stored) {
      setCurrentOffice(Number(stored));
    }
  }, []);

  useEffect(() => {
    if (currentOffice) {
      localStorage.setItem("currentOffice", currentOffice.toString());
    }
  }, [currentOffice]);

  return (
    <OfficeContext.Provider value={{ currentOffice, setCurrentOffice, office, isOfficeLoading }}>
      <OfficeAccessGuard>{props.children}</OfficeAccessGuard>
    </OfficeContext.Provider>
  );
};

export { useOfficeContext, OfficeProvider };
